'use client'
import Link from 'next/link'
import ImageComponent from '../../common/ImageComponent'
import { InstagramSVG } from '@/src/ui/AllSVG'
import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const INSTAGRAM_HANDLE = 'VALLOTE'
// const INSTAGRAM_URL = 'https://instagram.com/vallote'

interface InstagramTile {
    id: number
    src: string
    alt: string
    style: React.CSSProperties
}

const TILES: InstagramTile[] = [
    {
        id: 1,
        src: '/images/common/img1.svg',
        alt: 'Vallote editorial — large portrait',
        style: { left: '4%', top: '3%', width: '24%', height: '54%' },
    },
    {
        id: 2,
        src: '/images/common/img2.svg',
        alt: 'Vallote editorial — portrait',
        style: { left: '20%', top: '39%', width: '13%', height: '27%' },
    },
    {
        id: 3,
        src: '/images/common/img3.svg',
        alt: 'Vallote editorial — portrait close-up',
        style: { left: '62%', top: '2%', width: '7%', height: '20%' },
    },
    {
        id: 4,
        src: '/images/common/img4.svg',
        alt: 'Vallote editorial — accessory',
        style: { left: '54%', top: '77%', width: '6%', height: '21%' },
    },
    {
        id: 5,
        src: '/images/common/img5.svg',
        alt: 'Vallote editorial — bag',
        style: { left: '62%', top: '61%', width: '15%', height: '37%' },
    },
    {
        id: 6,
        src: '/images/common/img6.svg',
        alt: 'Vallote editorial — jewellery',
        style: { left: '83%', top: '38%', width: '17%', height: '35%' },
    },
]

// Per-tile float config — varied duration & amplitude for an organic feel
const FLOAT_CONFIG = [
    { y: 54, duration: 2.2 },
    { y: 9, duration: 2.7 },
    { y: 11, duration: 3.6 },
    { y: 10, duration: 1.5 },
    { y: 25, duration: 1.5 },
    { y: 32, duration: 2.4 },
]

export default function InstagramSection({ INSTAGRAM_URL }: { INSTAGRAM_URL: string }) {
    const sectionRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        if (!sectionRef.current) return

        const desktopTiles = sectionRef.current.querySelectorAll('.ig-tile')
        const mobileItems = sectionRef.current.querySelectorAll('.ig-mobile-item')
        const igHeader = sectionRef.current.querySelector('.ig-header')

        // ── Desktop entrance — scrubbed fade-in ──────────────────────────────
        gsap.timeline({
            scrollTrigger: {
                trigger: sectionRef.current,
                start: 'top 85%',
                end: 'top 20%',
                scrub: 1.2,
            },
        })
            .from(igHeader, {
                opacity: 0,
                y: 20,
                duration: 0.4,
                ease: 'none',
            })
            .from(
                desktopTiles,
                {
                    opacity: 0,
                    y: 60,
                    duration: 0.6,
                    stagger: 0.08,
                    ease: 'none',
                },
                '-=0.2',
            )

        // Float starts once tiles are fully in view (runs once, forever after)
        ScrollTrigger.create({
            trigger: sectionRef.current,
            start: 'top 25%',
            once: true,
            onEnter: () => {
                desktopTiles.forEach((tile, i) => {
                    const { y, duration } = FLOAT_CONFIG[i % FLOAT_CONFIG.length]
                    gsap.to(tile, {
                        y: `-=${y}`,
                        duration,
                        ease: 'sine.inOut',
                        yoyo: true,
                        repeat: -1,
                    })
                })
            },
        })

        // ── Scroll fade-out as section exits viewport ─────────────────────────
        gsap.to(sectionRef.current, {
            opacity: 0,
            scrollTrigger: {
                trigger: sectionRef.current,
                start: 'bottom 55%',
                end: 'bottom top',
                scrub: 1,
            },
        })

        // ── Mobile entrance — scrubbed fade-in ───────────────────────────────
        gsap.from(mobileItems, {
            opacity: 0,
            y: 30,
            stagger: 0.08,
            ease: 'none',
            scrollTrigger: {
                trigger: sectionRef.current,
                start: 'top 80%',
                end: 'top 25%',
                scrub: 1,
            },
        })
    }, { scope: sectionRef })

    return (
        <>
            <div className="invisible">
                <InstagramSVG />
            </div>
            <div ref={sectionRef} className="custom_container py-10">
                {/* Mobile grid — shown on small screens */}
                <div className="md:hidden space-y-4">
                    <div className="ig-mobile-item text-center space-y-1 mb-6">
                        <p className="uppercase text-body-color text-sm tracking-widest">Follow Our World</p>
                        <Link
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1.5 font-secondary"
                        >
                            <InstagramSVG />
                            {INSTAGRAM_HANDLE}
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {TILES.map((tile) => (
                            <Link
                                key={tile.id}
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ig-mobile-item overflow-hidden rounded-xl aspect-square"
                            >
                                <div className="w-full h-full relative">
                                    <ImageComponent
                                        src={tile.src}
                                        alt={tile.alt}
                                        object_cover
                                        priority={false}
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Desktop collage — hidden on small screens */}
                <div className="hidden md:block relative w-full h-[600px]">
                    <div className="absolute inset-0 rounded-xl overflow-visible">
                        {TILES.map((tile) => (
                            <Link
                                key={tile.id}
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ig-tile absolute overflow-hidden will-change-transform"
                                style={tile.style}
                            >
                                <div className="w-full h-full relative rounded-lg overflow-hidden">
                                    <ImageComponent
                                        src={tile.src}
                                        alt={tile.alt}
                                        object_cover
                                        priority={false}
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div
                        className="ig-header absolute inset-0 flex flex-col items-center justify-center select-none gap-2"
                        style={{ zIndex: 10 }}
                    >
                        <p className="uppercase text-body-color">Follow Our World</p>
                        <Link
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-xl font-secondary"
                        >
                            <InstagramSVG />
                            {INSTAGRAM_HANDLE}
                        </Link>
                    </div>
                </div>
            </div>
        </>
    )
}
